/**
 * Bare PSI wrapper around react-aria-components' `Popover`. Intended as a
 * building block for higher-level popover and menu wrappers, not for direct use
 * in product code.
 */
import { useTheme } from '@repo/theme';
import { cva, cx } from 'class-variance-authority';
import { spacings } from 'component/constants';
import { Popover as AriaPopover, type PopoverProps as AriaPopoverProps } from 'react-aria-components';

import { BaseOverlayArrow } from './base-overlay-arrow';
import styles from './base-popover.module.css';

export type BasePopoverProps = {
    showArrow?: boolean;
    children: React.ReactNode;
    popoverClassName?: string;
    arrowClassName?: string;
    spacing?: 'default' | 'tight';
    dark?: boolean;
} & Omit<AriaPopoverProps, 'children' | 'className'>;

const DEFAULT_OFFSET = spacings.sm;

const popover = cva(styles.popover, {
    variants: {
        dark: {
            true: styles.dark,
            false: styles.light,
        },
        spacing: {
            default: styles.default,
            tight: styles.tight,
        },
    },
});

export function BasePopover(props: BasePopoverProps) {
    const {
        children,
        spacing = 'default',
        dark = false,
        placement = 'top',
        showArrow = false,
        popoverClassName,
        arrowClassName,
        ...rest
    } = props;
    const { colors } = useTheme();
    const arrowClass = cx(styles.arrow, arrowClassName);

    return (
        <AriaPopover
            data-testid='base-popover'
            offset={DEFAULT_OFFSET}
            placement={placement}
            {...rest}
            className={popover({ dark, spacing, className: popoverClassName })}
        >
            {showArrow && (
                <BaseOverlayArrow className={arrowClass} fill={dark ? colors.colorSurfacePopoverDark : colors.colorSurfacePrimary} />
            )}
            {children}
        </AriaPopover>
    );
}
